import { useCallback, useRef, useState } from 'react';
import { Modal, message } from 'antd';
import axios from 'axios';
import { bridgeSessionService } from '../../services/bridgeSessionService';
import {
  IntentMLService,
  IntentMLImportValidationError,
} from '../../services/api/intentmlService';
import { parseJsonFileToRecord } from '../../utils/jsonFile';

function fastApiDetail(err: unknown): string {
  if (axios.isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    const d = err.response.data as { detail?: unknown };
    if (typeof d.detail === 'string') return d.detail;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function downloadJsonFile(data: Record<string, unknown>, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function useBridgeSessionIntentmlFacilitator(sessionId: string, companyId?: string | null) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importErrors, setImportErrors] = useState<{ code: string; path: string; message: string }[] | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [completeLoading, setCompleteLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [lastIntentmlUrl, setLastIntentmlUrl] = useState<string | null>(null);

  const onCompleteSession = useCallback(() => {
    Modal.confirm({
      title: 'Complete bridge session?',
      content:
        'This closes the session on the server and stores an IntentML snapshot. You cannot complete again for the same session.',
      okText: 'Complete session',
      onOk: async () => {
        setCompleteLoading(true);
        try {
          const res = await bridgeSessionService.completeSession(sessionId);
          setLastIntentmlUrl(res.intentml_url);
          message.success('Session completed. IntentML snapshot is ready to download.');
        } catch (err: unknown) {
          if (axios.isAxiosError(err) && err.response?.status === 409) {
            message.info('Session was already closed. Try Download IntentML JSON if a snapshot exists.');
          } else {
            message.error(fastApiDetail(err));
          }
        } finally {
          setCompleteLoading(false);
        }
      },
    });
  }, [sessionId]);

  const onDownloadSnapshot = useCallback(async () => {
    setDownloadLoading(true);
    try {
      const data = await bridgeSessionService.getIntentmlSnapshot(sessionId);
      const safeCompany = companyId?.replace(/[^a-zA-Z0-9_-]/g, '') || 'company';
      const date = new Date().toISOString().slice(0, 10);
      downloadJsonFile(data, `intentml_${safeCompany}_${date}.json`);
      message.success('Download started.');
    } catch (err: unknown) {
      message.error(fastApiDetail(err));
    } finally {
      setDownloadLoading(false);
    }
  }, [sessionId, companyId]);

  const onPickImportFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onImportFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setImportErrors(null);
    let doc: Record<string, unknown>;
    try {
      doc = await parseJsonFileToRecord(file);
    } catch {
      message.error('Invalid JSON file.');
      return;
    }

    setImportLoading(true);
    try {
      const result = await IntentMLService.importCompanyDocument(doc, companyId ?? undefined);
      message.success(
        `Import ${result.import_id}: ${result.artifacts_updated} artifacts, ${result.artifact_items_updated} items updated.`
      );
    } catch (err: unknown) {
      if (err instanceof IntentMLImportValidationError) {
        setImportErrors(err.errors);
        message.error('Import validation failed.');
      } else {
        message.error(fastApiDetail(err));
      }
    } finally {
      setImportLoading(false);
    }
  }, [companyId]);

  return {
    fileInputRef,
    importErrors,
    importLoading,
    completeLoading,
    downloadLoading,
    lastIntentmlUrl,
    onCompleteSession,
    onDownloadSnapshot,
    onPickImportFile,
    onImportFileChange,
  };
}
